import { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  getWatchBrands,
  getExistingReferenceMap,
  getLookupCollection,
} from '../../services/firebase';
import type { WatchBrand, WatchReference, LookupValue } from '../../types';
import { Button } from '../common/Button';
import { BrandFilterSelect } from './BrandFilterSelect';

type Mode = 'brand' | 'reference' | 'filtered';
type Confidence = 'High' | 'Medium' | 'Low';

interface Suggestion {
  refId: string;
  reference: string;
  model?: string;
  current: string[];
  added: string[]; // newly-derived occasions (not already in current)
  merged: string[]; // current ∪ added
  confidence: Confidence;
  reasons: string[];
}

// Known model-name → occasion hints (case-insensitive substring matches)
const MODEL_OCCASION_HINTS: Record<string, string[]> = {
  'submariner': ['diver'],
  'seamaster': ['diver'],
  'aqua terra': ['diver', 'dress'],
  'navitimer': ['pilot', 'aviation', 'chronograph'],
  'speedmaster': ['chronograph', 'sports'],
  'datejust': ['dress'],
  'day-date': ['dress', 'luxury'],
  'royal oak': ['luxury', 'sports'],
  'nautilus': ['luxury', 'sports'],
  'tank': ['dress', 'luxury'],
  'santos': ['pilot', 'aviation'],
  'reverso': ['dress'],
  'master': ['dress'],
  'pilot': ['pilot', 'aviation'],
  'avenger': ['pilot', 'tool'],
  'superocean': ['diver'],
  'pelagos': ['diver', 'tool'],
  'black bay': ['diver', 'heritage'],
  'ranger': ['field', 'tool'],
  'explorer': ['explorer', 'tool'],
  'milgauss': ['tool'],
  'gmt-master': ['gmt', 'travel'],
  'sky-dweller': ['gmt', 'travel', 'luxury'],
  'endurance': ['sports', 'tool'],
  'khaki field': ['field', 'military'],
  'khaki aviation': ['pilot', 'aviation'],
  'big pilot': ['pilot', 'aviation'],
  'flieger': ['flieger', 'pilot'],
  'diver': ['diver'],
  'dive': ['diver'],
  'chrono': ['chronograph'],
  'racing': ['racing', 'chronograph'],
  'sport': ['sports'],
  'field': ['field'],
  'military': ['military'],
  'dress': ['dress'],
  // Casio
  'g-shock': ['tool', 'digital', 'sports'],
  'gshock': ['tool', 'digital', 'sports'],
  'baby-g': ['sports', 'digital'],
  'pro trek': ['outdoor', 'digital', 'tool'],
  'protrek': ['outdoor', 'digital', 'tool'],
  'edifice': ['chronograph', 'sports'],
  'oceanus': ['luxury', 'sports'],
  'mudmaster': ['tool', 'outdoor', 'military'],
  'rangeman': ['tool', 'outdoor', 'military'],
  'frogman': ['diver', 'tool', 'digital'],
  'gravitymaster': ['pilot', 'aviation', 'tool'],
  'mudman': ['tool', 'outdoor'],
  // Other digital brands
  'instinct': ['outdoor', 'digital', 'tool'],
  'fenix': ['outdoor', 'sports', 'digital'],
  'tactix': ['tactical', 'digital', 'military'],
  'marq': ['luxury', 'smart'],
  // General
  'digital': ['digital'],
  'solar': ['everyday', 'tool'],
  'tough solar': ['everyday', 'tool'],
  'eco-drive': ['everyday'],
};

// Broader keyword hints — Medium confidence substring matches for model/collection/productName
// These are generic descriptive words (not brand-specific model names) that suggest an occasion
const KEYWORD_OCCASION_HINTS: Record<string, string[]> = {
  // Diver
  'sea': ['diver'],
  'ocean': ['diver'],
  'aqua': ['diver'],
  'marine': ['diver'],
  'shark': ['diver'],
  'reef': ['diver'],
  'pearl': ['diver'],
  'pelagic': ['diver'],
  'neptune': ['diver'],
  'abyss': ['diver'],
  'deep': ['diver'],
  'fathom': ['diver'],
  'surf': ['diver'],
  'hydro': ['diver'],
  'tidal': ['diver'],
  'wave': ['diver'],
  'marlin': ['diver'],
  'barracuda': ['diver'],
  'turtle': ['diver'],
  'triton': ['diver'],
  // Pilot / Aviation
  'sky': ['pilot', 'aviation'],
  'aero': ['pilot', 'aviation'],
  'flight': ['pilot', 'aviation'],
  'cockpit': ['pilot', 'aviation'],
  'airman': ['pilot', 'aviation'],
  'altitude': ['pilot', 'aviation'],
  'horizon': ['pilot', 'aviation'],
  'wings': ['pilot', 'aviation'],
  'propeller': ['pilot', 'aviation'],
  // Field / Outdoor
  'trail': ['field', 'outdoor'],
  'expedition': ['field', 'outdoor', 'adventure'],
  'rambler': ['field', 'outdoor'],
  'scout': ['field', 'outdoor'],
  'patrol': ['field', 'military'],
  'terrain': ['field', 'outdoor'],
  'summit': ['outdoor', 'mountaineering'],
  'ridge': ['outdoor', 'mountaineering'],
  'peak': ['outdoor', 'mountaineering'],
  'alpine': ['outdoor', 'mountaineering'],
  'basecamp': ['outdoor', 'adventure'],
  // Military / Tactical
  'combat': ['military', 'tactical'],
  'commando': ['military', 'tactical'],
  'recon': ['military', 'tactical'],
  'stealth': ['military', 'tactical'],
  'ops': ['military', 'tactical'],
  'tactical': ['tactical', 'military'],
  'trench': ['military', 'heritage'],
  // Racing / Sports
  'rally': ['racing', 'sports'],
  'enduro': ['racing', 'sports'],
  'sprint': ['racing', 'sports'],
  'velocity': ['racing', 'sports'],
  'track': ['racing', 'sports'],
  'monaco': ['racing', 'chronograph'],
  'daytona': ['racing', 'chronograph'],
  'carrera': ['racing', 'chronograph'],
  // Dress / Luxury
  'elegance': ['dress'],
  'classique': ['dress', 'classic'],
  'tradition': ['dress', 'heritage'],
  'prestige': ['dress', 'luxury'],
  'grand': ['luxury'],
  'royal': ['luxury'],
  'patrimony': ['dress', 'heritage', 'luxury'],
  // Sailing / Yacht
  'regatta': ['sailing', 'regatta'],
  'yacht': ['yacht', 'sailing'],
  'skipper': ['sailing'],
  'helm': ['sailing'],
  // Travel
  'traveler': ['travel', 'gmt'],
  'traveller': ['travel', 'gmt'],
  'world': ['world_time', 'travel'],
  'globemaster': ['travel', 'gmt'],
};

// watchType field → additional occasion tags (beyond alias matching)
const WATCH_TYPE_OCCASION_MAP: Record<string, string[]> = {
  'digital': ['digital'],
  'ana-digi': ['ana_digi', 'digital'],
  'ana_digi': ['ana_digi', 'digital'],
  'analog-digital': ['ana_digi', 'digital'],
  'smartwatch': ['smart', 'digital'],
  'connected_quartz': ['smart', 'hybrid'],
  'hybrid': ['hybrid'],
};

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Build a list of (value, alias) pairs used to match text fields. */
interface AliasEntry {
  value: string; // canonical occasion value (e.g., "diver")
  alias: string; // the searchable string (canonical OR synonym OR displayName)
  isCanonical: boolean; // true if alias === canonical value or displayName
}

function buildAliasEntries(lookupValues: LookupValue[]): AliasEntry[] {
  const entries: AliasEntry[] = [];
  for (const lv of lookupValues) {
    if (!lv.isActive) continue;
    const canonical = lv.value;
    const seen = new Set<string>();
    const push = (alias: string, isCanonical: boolean) => {
      const a = alias.trim();
      if (!a) return;
      const key = a.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      entries.push({ value: canonical, alias: a, isCanonical });
    };
    push(canonical, true);
    if (lv.displayName) push(lv.displayName, true);
    if (lv.synonyms) for (const syn of lv.synonyms) push(syn, false);
  }
  return entries;
}

/** Word-boundary match of `alias` inside `text` (case-insensitive). */
function aliasMatchesText(alias: string, text: string): boolean {
  if (!text || !alias) return false;
  // Replace internal whitespace with \s+ for phrase matching; use \b at edges
  const escaped = escapeRegex(alias).replace(/\\\s+/g, '\\s+').replace(/\s+/g, '\\s+');
  // \b only works for alphanumeric edges. For tokens that start/end with non-word
  // chars (e.g., "gmt-master"), fall back to non-letter boundaries.
  const startBoundary = /^[A-Za-z0-9]/.test(alias) ? '\\b' : '(?:^|[^A-Za-z0-9])';
  const endBoundary = /[A-Za-z0-9]$/.test(alias) ? '\\b' : '(?:[^A-Za-z0-9]|$)';
  try {
    const re = new RegExp(`${startBoundary}${escaped}${endBoundary}`, 'i');
    return re.test(text);
  } catch {
    return text.toLowerCase().includes(alias.toLowerCase());
  }
}

/** Get water resistance in meters, handling several field shapes. */
function getWaterResistanceM(ref: WatchReference): number | undefined {
  const c = ref.case as { waterResistanceM?: number; waterResistance?: string | number } | undefined;
  if (!c) return undefined;
  if (typeof c.waterResistanceM === 'number') return c.waterResistanceM;
  const raw = c.waterResistance;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    // Try "200m", "20 ATM", "200" etc.
    const atmMatch = raw.match(/(\d+(?:\.\d+)?)\s*atm/i);
    if (atmMatch) return parseFloat(atmMatch[1]) * 10;
    const mMatch = raw.match(/(\d+(?:\.\d+)?)\s*m/i);
    if (mMatch) return parseFloat(mMatch[1]);
    const num = parseFloat(raw);
    if (!Number.isNaN(num)) return num;
  }
  return undefined;
}

function parseCurrentOccasion(raw: WatchReference['occasion']): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string' && !!v.trim());
  if (typeof raw === 'string') return raw.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
  return [];
}

/** Run the extraction for a single reference. Returns null if no suggestions. */
function extractOccasionsFor(
  ref: WatchReference,
  aliases: AliasEntry[],
): Suggestion | null {
  const current = parseCurrentOccasion(ref.occasion);
  // Normalize current values (lowercase, trimmed) for comparison
  const currentSet = new Set(current.map((v) => v.toLowerCase()));

  const hits = new Map<string, { confidence: Confidence; reason: string }>();

  const addHit = (value: string, confidence: Confidence, reason: string) => {
    const key = value.toLowerCase();
    const existing = hits.get(key);
    if (!existing) {
      hits.set(key, { confidence, reason });
      return;
    }
    // Upgrade confidence (High > Medium > Low)
    const rank = { High: 3, Medium: 2, Low: 1 } as const;
    if (rank[confidence] > rank[existing.confidence]) {
      hits.set(key, { confidence, reason });
    }
  };

  // 1) functions array — exact match against any alias (canonical or synonym)
  if (Array.isArray(ref.functions)) {
    for (const fn of ref.functions) {
      if (typeof fn !== 'string') continue;
      const fnLower = fn.trim().toLowerCase();
      if (!fnLower) continue;
      for (const a of aliases) {
        if (a.alias.toLowerCase() === fnLower) {
          addHit(a.value, 'High', `functions contains "${fn}"`);
        }
      }
    }
  }

  // 2) model / collection / productName — High when canonical alias matches,
  //    Medium when synonym matches.
  const strongTextFields: Array<{ name: string; text: string }> = [
    { name: 'model', text: ref.model || '' },
    { name: 'collection', text: ref.collection || '' },
    { name: 'productName', text: ref.productName || '' },
  ];

  for (const field of strongTextFields) {
    if (!field.text) continue;
    for (const a of aliases) {
      if (aliasMatchesText(a.alias, field.text)) {
        addHit(
          a.value,
          a.isCanonical ? 'High' : 'Medium',
          `${field.name} matches "${a.alias}"`,
        );
      }
    }
  }

  // 3) description / aiDescription — Medium for canonical, Medium for synonym
  const descFields: Array<{ name: string; text: string }> = [
    { name: 'description', text: ref.description || '' },
    { name: 'aiDescription', text: ref.aiDescription || '' },
  ];
  for (const field of descFields) {
    if (!field.text) continue;
    for (const a of aliases) {
      if (aliasMatchesText(a.alias, field.text)) {
        addHit(a.value, 'Medium', `${field.name} matches "${a.alias}"`);
      }
    }
  }

  // 4) watchType field — match against aliases AND compound type map
  if (ref.watchType) {
    const wt = ref.watchType.trim().toLowerCase();
    // Alias matching (existing)
    for (const a of aliases) {
      if (a.alias.toLowerCase() === wt) {
        addHit(a.value, 'High', `watchType is "${ref.watchType}"`);
      }
    }
    // Compound type mapping
    const mappedOccasions = WATCH_TYPE_OCCASION_MAP[wt];
    if (mappedOccasions) {
      for (const occ of mappedOccasions) {
        addHit(occ, 'High', `watchType "${ref.watchType}" maps to ${occ}`);
      }
    }
  }

  // 5) MODEL_OCCASION_HINTS — case-insensitive substring against model + collection + productName
  const combinedNameText = `${ref.model || ''} ${ref.collection || ''} ${ref.productName || ''}`.toLowerCase();
  for (const [keyword, occasions] of Object.entries(MODEL_OCCASION_HINTS)) {
    if (combinedNameText.includes(keyword)) {
      for (const occ of occasions) {
        addHit(occ, 'High', `model name contains "${keyword}"`);
      }
    }
  }

  // 5b) KEYWORD_OCCASION_HINTS — broader keyword matches, Medium confidence
  for (const [keyword, occasions] of Object.entries(KEYWORD_OCCASION_HINTS)) {
    // Use word boundary matching to avoid false positives (e.g. "pearl" shouldn't match "appears")
    const regex = new RegExp(`\\b${escapeRegex(keyword)}\\b`, 'i');
    if (regex.test(combinedNameText)) {
      for (const occ of occasions) {
        addHit(occ, 'Medium', `keyword "${keyword}" in model/collection/product name`);
      }
    }
  }

  // 6) Water resistance ≥ 200m → diver (Low, only added if no other strong hit yet)
  const wrM = getWaterResistanceM(ref);
  if (wrM !== undefined && wrM >= 200) {
    if (!hits.has('diver')) {
      addHit('diver', 'Low', `case.waterResistance ≥ 200m (${wrM}m)`);
    }
  }

  // 7) Quartz movement → everyday (Low)
  const movementType = (ref.movementType || '').toLowerCase();
  if (movementType.includes('quartz')) {
    if (!hits.has('everyday')) {
      addHit('everyday', 'Low', 'movementType is quartz');
    }
  }

  // Filter out hits that are already in current
  const added: string[] = [];
  const reasons: string[] = [];
  let maxRank = 0;
  const rank = { High: 3, Medium: 2, Low: 1 } as const;
  let topConfidence: Confidence = 'Low';
  for (const [key, { confidence, reason }] of hits) {
    if (currentSet.has(key)) continue;
    added.push(key);
    reasons.push(`${key}: ${reason}`);
    if (rank[confidence] > maxRank) {
      maxRank = rank[confidence];
      topConfidence = confidence;
    }
  }

  if (added.length === 0) return null;

  return {
    refId: ref.id,
    reference: ref.reference,
    model: ref.model,
    current,
    added,
    merged: [...current, ...added],
    confidence: topConfidence,
    reasons,
  };
}

const WATCH_BRANDS_COLLECTION = 'watchBrands';
const REFERENCES_SUBCOLLECTION = 'references';

interface BrandResults {
  brandId: string;
  brandName: string;
  suggestions: Suggestion[];
}

interface BulkOccasionExtractorProps {
  /** Pre-select this brand on mount (deep-link from the Enrichment Tasks panel). */
  initialBrandId?: string;
  /** Called once the initial brand has been applied, so the parent can clear it. */
  onBrandConsumed?: () => void;
  /** When true the tool is hosted inside the per-brand panel's modal: hide the
   *  page header and brand picker (brand is locked via initialBrandId). */
  embedded?: boolean;
}

export function BulkOccasionExtractor({
  initialBrandId,
  onBrandConsumed,
  embedded = false,
}: BulkOccasionExtractorProps = {}) {
  const [mode, setMode] = useState<Mode>('brand');
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  // Seed the brand picker once when deep-linked from the per-brand panel.
  useEffect(() => {
    if (initialBrandId) {
      setSelectedBrandId(initialBrandId);
      onBrandConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);
  const [textFilter, setTextFilter] = useState('');
  const [refSearch, setRefSearch] = useState('');
  const [selectedRefIds, setSelectedRefIds] = useState<Set<string>>(new Set());
  const [brandReferences, setBrandReferences] = useState<WatchReference[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [aliases, setAliases] = useState<AliasEntry[]>([]);
  const [loadingLookup, setLoadingLookup] = useState(true);
  const [scanResult, setScanResult] = useState<BrandResults | null>(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  // Load brands once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getWatchBrands();
        if (!cancelled) {
          setBrands(
            list.sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      } catch (err) {
        console.error('Failed to load brands', err);
        toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load occasion lookup once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lvs = await getLookupCollection('lookup_watch_occasion');
        if (!cancelled) setAliases(buildAliasEntries(lvs));
      } catch (err) {
        console.error('Failed to load occasion lookup', err);
        toast.error('Failed to load occasion lookup');
      } finally {
        if (!cancelled) setLoadingLookup(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load references when brand changes
  useEffect(() => {
    if (!selectedBrandId) {
      setBrandReferences([]);
      setSelectedRefIds(new Set());
      setScanResult(null);
      return;
    }
    let cancelled = false;
    setLoadingRefs(true);
    setScanResult(null);
    setSelectedRefIds(new Set());
    (async () => {
      try {
        const map = await getExistingReferenceMap(selectedBrandId);
        const refs = Array.from(map.values()).sort((a, b) =>
          (a.reference || '').localeCompare(b.reference || ''),
        );
        if (!cancelled) setBrandReferences(refs);
      } catch (err) {
        console.error('Failed to load references for brand', err);
        toast.error('Failed to load references');
      } finally {
        if (!cancelled) setLoadingRefs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId]);

  // Reset state when mode changes
  useEffect(() => {
    setScanResult(null);
    setSelectedSuggestions(new Set());
    setSelectedRefIds(new Set());
    setTextFilter('');
    setRefSearch('');
  }, [mode]);

  const filteredRefsForPicker = useMemo(() => {
    const term = refSearch.trim().toLowerCase();
    if (!term) return brandReferences.slice(0, 200);
    return brandReferences
      .filter((r) => {
        const fields = [r.reference, r.model, r.productName, r.collection];
        return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(term));
      })
      .slice(0, 200);
  }, [brandReferences, refSearch]);

  const referencesToProcess = useMemo<WatchReference[]>(() => {
    if (!selectedBrandId) return [];
    if (mode === 'brand') return brandReferences;
    if (mode === 'reference') {
      return brandReferences.filter((r) => selectedRefIds.has(r.id));
    }
    // filtered
    const term = textFilter.trim().toLowerCase();
    if (!term) return brandReferences;
    return brandReferences.filter((r) => {
      const fields = [r.reference, r.model, r.productName, r.collection, r.description];
      return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(term));
    });
  }, [mode, selectedBrandId, brandReferences, selectedRefIds, textFilter]);

  const handleScan = useCallback(async () => {
    if (!selectedBrandId) {
      toast.error('Select a brand first');
      return;
    }
    if (loadingLookup) {
      toast.error('Occasion lookup still loading');
      return;
    }
    if (aliases.length === 0) {
      toast.error('No occasion values available');
      return;
    }
    const brand = brands.find((b) => b.id === selectedBrandId);
    if (!brand) return;

    setScanning(true);
    try {
      // Use rAF chunking so we don't block the UI for large brands
      const suggestions: Suggestion[] = [];
      const refs = referencesToProcess;
      const chunkSize = 200;
      for (let i = 0; i < refs.length; i += chunkSize) {
        const slice = refs.slice(i, i + chunkSize);
        for (const r of slice) {
          const s = extractOccasionsFor(r, aliases);
          if (s) suggestions.push(s);
        }
        if (i + chunkSize < refs.length) {
          await new Promise((res) => setTimeout(res, 0));
        }
      }
      // Sort: High → Medium → Low, then by reference
      const rank = { High: 3, Medium: 2, Low: 1 } as const;
      suggestions.sort((a, b) => {
        const r = rank[b.confidence] - rank[a.confidence];
        if (r !== 0) return r;
        return (a.reference || '').localeCompare(b.reference || '');
      });
      setScanResult({
        brandId: brand.id,
        brandName: brand.name,
        suggestions,
      });
      // Pre-select all High and Medium confidence rows by default
      const preselected = new Set<string>();
      for (const s of suggestions) {
        if (s.confidence !== 'Low') preselected.add(s.refId);
      }
      setSelectedSuggestions(preselected);
      if (suggestions.length === 0) {
        toast(`No new occasions detected across ${refs.length} reference${refs.length === 1 ? '' : 's'}`);
      } else {
        toast.success(`Found ${suggestions.length} reference${suggestions.length === 1 ? '' : 's'} with new occasions`);
      }
    } catch (err) {
      console.error('Scan failed', err);
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  }, [aliases, brands, loadingLookup, referencesToProcess, selectedBrandId]);

  const handleToggleSelection = (refId: string) => {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(refId)) next.delete(refId);
      else next.add(refId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!scanResult) return;
    setSelectedSuggestions(new Set(scanResult.suggestions.map((s) => s.refId)));
  };

  const handleDeselectAll = () => {
    setSelectedSuggestions(new Set());
  };

  const handleApply = async () => {
    if (!scanResult) return;
    const toApply = scanResult.suggestions.filter((s) => selectedSuggestions.has(s.refId));
    if (toApply.length === 0) {
      toast.error('Nothing selected');
      return;
    }
    setApplying(true);
    let success = 0;
    let failed = 0;
    for (const s of toApply) {
      try {
        const docRef = doc(
          db,
          WATCH_BRANDS_COLLECTION,
          scanResult.brandId,
          REFERENCES_SUBCOLLECTION,
          s.refId,
        );
        await updateDoc(docRef, {
          occasion: s.merged.join(', '),
          updatedAt: Timestamp.now(),
        });
        success++;
      } catch (err) {
        console.error('Failed to update reference', s.refId, err);
        failed++;
      }
    }
    setApplying(false);
    const appliedMap = new Map(toApply.map((s) => [s.refId, s.merged]));
    setBrandReferences((prev) =>
      prev.map((ref) => {
        const merged = appliedMap.get(ref.id);
        if (merged) {
          return { ...ref, occasion: merged.join(', ') };
        }
        return ref;
      }),
    );
    if (failed === 0) {
      toast.success(`Updated ${success} reference${success === 1 ? '' : 's'}`);
    } else {
      toast.error(`Updated ${success}, ${failed} failed`);
    }
    // Remove applied rows from the result
    setScanResult((prev) =>
      prev
        ? {
            ...prev,
            suggestions: prev.suggestions.filter((s) => !selectedSuggestions.has(s.refId)),
          }
        : prev,
    );
    setSelectedSuggestions(new Set());
  };

  const handleToggleRefId = (refId: string) => {
    setSelectedRefIds((prev) => {
      const next = new Set(prev);
      if (next.has(refId)) next.delete(refId);
      else next.add(refId);
      return next;
    });
  };

  const modeButtonClass = (m: Mode) =>
    `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
      mode === m
        ? 'bg-brand-600 text-white'
        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
    }`;

  const confidenceBadgeClass = (c: Confidence) => {
    if (c === 'High') return 'bg-green-100 text-green-800';
    if (c === 'Medium') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Occasion Tagger</h1>
          <p className="text-gray-500 mt-1">
            Scan references and auto-populate the <code className="font-mono text-sm">occasion</code> field by matching
            functions, model names, descriptions, and watch type against known occasion values.
          </p>
        </div>
      )}

      {/* Mode selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex gap-2">
          <button onClick={() => setMode('brand')} className={modeButtonClass('brand')}>
            By Brand
          </button>
          <button onClick={() => setMode('reference')} className={modeButtonClass('reference')}>
            By Reference
          </button>
          <button onClick={() => setMode('filtered')} className={modeButtonClass('filtered')}>
            Filtered
          </button>
        </div>

        {/* Brand picker (used in all modes; hidden when brand is locked) */}
        {!embedded && (
          <BrandFilterSelect
            brands={brands}
            loading={loadingBrands}
            value={selectedBrandId}
            onChange={setSelectedBrandId}
          />
        )}

        {/* Mode-specific controls */}
        {mode === 'reference' && selectedBrandId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search references ({brandReferences.length} total, {selectedRefIds.size} selected)
            </label>
            <input
              type="text"
              value={refSearch}
              onChange={(e) => setRefSearch(e.target.value)}
              placeholder="Search by reference, model, productName, or collection"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
              {loadingRefs && <div className="p-3 text-sm text-gray-500">Loading…</div>}
              {!loadingRefs && filteredRefsForPicker.length === 0 && (
                <div className="p-3 text-sm text-gray-500">No references match.</div>
              )}
              {filteredRefsForPicker.map((r) => (
                <label
                  key={r.id}
                  className="flex items-start gap-2 p-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedRefIds.has(r.id)}
                    onChange={() => handleToggleRefId(r.id)}
                    className="mt-1"
                  />
                  <span className="flex-1">
                    <span className="font-mono">{r.reference}</span>
                    {r.model && <span className="text-gray-600"> — {r.model}</span>}
                    {r.collection && <span className="text-gray-400"> ({r.collection})</span>}
                  </span>
                </label>
              ))}
              {!loadingRefs && brandReferences.length > filteredRefsForPicker.length && (
                <div className="p-2 text-xs text-gray-500 italic">
                  Showing first {filteredRefsForPicker.length} of {brandReferences.length} — refine search to see more.
                </div>
              )}
            </div>
          </div>
        )}

        {mode === 'filtered' && selectedBrandId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Text filter (matches reference, model, productName, collection, description)
            </label>
            <input
              type="text"
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder="Leave empty to scan all"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {brandReferences.length} total references — {referencesToProcess.length} will be scanned.
            </p>
          </div>
        )}

        {mode === 'brand' && selectedBrandId && !loadingRefs && (
          <p className="text-xs text-gray-500">
            {brandReferences.length} reference{brandReferences.length === 1 ? '' : 's'} will be scanned.
          </p>
        )}

        <div className="pt-2">
          <Button
            onClick={handleScan}
            disabled={
              !selectedBrandId ||
              loadingRefs ||
              loadingLookup ||
              scanning ||
              (mode === 'reference' && selectedRefIds.size === 0)
            }
            loading={scanning}
          >
            Scan for occasions
          </Button>
        </div>
      </div>

      {/* Results */}
      {scanResult && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {scanResult.brandName} — {scanResult.suggestions.length} suggestion
                {scanResult.suggestions.length === 1 ? '' : 's'}
              </h2>
              <p className="text-xs text-gray-500">
                {selectedSuggestions.size} selected for apply
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
                Deselect All
              </Button>
              <Button
                onClick={handleApply}
                disabled={selectedSuggestions.size === 0 || applying}
                loading={applying}
              >
                Apply Selected ({selectedSuggestions.size})
              </Button>
            </div>
          </div>

          {scanResult.suggestions.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 italic">
              No new occasions detected.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Model</th>
                    <th className="px-3 py-2 text-left">Current</th>
                    <th className="px-3 py-2 text-left">Suggested (merged)</th>
                    <th className="px-3 py-2 text-left">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {scanResult.suggestions.map((s) => (
                    <tr
                      key={s.refId}
                      className={selectedSuggestions.has(s.refId) ? 'bg-brand-50/50' : ''}
                    >
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={selectedSuggestions.has(s.refId)}
                          onChange={() => handleToggleSelection(s.refId)}
                        />
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-xs">{s.reference}</td>
                      <td className="px-3 py-2 align-top">{s.model || '—'}</td>
                      <td className="px-3 py-2 align-top text-gray-500">
                        {s.current.length > 0 ? s.current.join(', ') : '—'}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-wrap gap-1">
                          {s.merged.map((v) => {
                            const isNew = !s.current.some((c) => c.toLowerCase() === v.toLowerCase());
                            return (
                              <span
                                key={v}
                                className={`px-2 py-0.5 rounded-full text-xs ${
                                  isNew
                                    ? 'bg-brand-100 text-brand-700 font-medium'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {v}
                              </span>
                            );
                          })}
                        </div>
                        {s.reasons.length > 0 && (
                          <details className="mt-1">
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                              Why?
                            </summary>
                            <ul className="text-xs text-gray-500 mt-1 list-disc list-inside">
                              {s.reasons.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${confidenceBadgeClass(
                            s.confidence,
                          )}`}
                        >
                          {s.confidence}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
